import React, { useEffect, useState } from 'react';
import { 
  MessageCircle, Users, TrendingUp, Phone, Mail, Clock, 
  CheckCircle, XCircle, RefreshCw, Search, Filter, Save,
  ExternalLink, Trash2, Edit2
} from 'lucide-react';
import api from '../../utils/api';
import toast from 'react-hot-toast';

const WhatsAppLeads = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('leads'); // leads, settings
  const [stats, setStats] = useState(null);
  const [leads, setLeads] = useState([]);
  const [statusFilter, setStatusFilter] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  
  const [settings, setSettings] = useState({
    enabled: true,
    business_phone: '',
    business_name: 'ANANT WEB PORTAL',
    welcome_message: "Hi! I'm interested in learning more about your services.",
    auto_reply_message: "Thank you for contacting us! We'll respond within 24 hours.",
    working_hours: 'Mon-Sat, 9 AM - 6 PM',
    query_types: [
      { id: 'general', label: 'General Inquiry', template: 'Hi, I have a general inquiry.' },
      { id: 'support', label: 'Customer Support', template: 'Hi, I need help with my order/account.' },
      { id: 'sales', label: 'Sales Query', template: "Hi, I'm interested in purchasing." },
      { id: 'feedback', label: 'Feedback', template: "Hi, I'd like to share feedback." }
    ]
  });

  useEffect(() => {
    fetchData();
  }, [statusFilter]);

  const fetchData = async () => {
    try {
      const [statsRes, leadsRes, settingsRes] = await Promise.all([
        api.get('/admin/whatsapp/stats'),
        api.get(`/admin/whatsapp/leads${statusFilter ? `?status=${statusFilter}` : ''}`),
        api.get('/admin/whatsapp/settings')
      ]);
      
      setStats(statsRes.data);
      setLeads(leadsRes.data.leads || []);
      if (settingsRes.data) {
        setSettings(prev => ({ ...prev, ...settingsRes.data }));
      }
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveSettings = async () => {
    setSaving(true);
    try {
      await api.put('/admin/whatsapp/settings', settings);
      toast.success('Settings saved successfully!');
    } catch (error) {
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateLeadStatus = async (leadId, newStatus) => {
    try {
      await api.put(`/admin/whatsapp/leads/${leadId}?status=${newStatus}`);
      toast.success('Lead status updated');
      fetchData();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const handleDeleteLead = async (leadId) => {
    if (!window.confirm('Are you sure you want to delete this lead?')) return;
    
    try {
      await api.delete(`/admin/whatsapp/leads/${leadId}`);
      toast.success('Lead deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete lead');
    }
  };

  const filteredLeads = leads.filter(lead => 
    !searchQuery || 
    lead.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    lead.phone?.includes(searchQuery) ||
    lead.email?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const getStatusColor = (status) => {
    switch (status) {
      case 'new': return 'bg-blue-500/20 text-blue-500';
      case 'contacted': return 'bg-yellow-500/20 text-yellow-500';
      case 'converted': return 'bg-green-500/20 text-green-500';
      case 'closed': return 'bg-gray-500/20 text-gray-500';
      default: return 'bg-gray-500/20 text-gray-500';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen pt-24 pb-16 px-6 flex items-center justify-center">
        <div className="text-2xl">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-24 pb-16 px-6" data-testid="whatsapp-leads-page">
      <div className="max-w-7xl mx-auto">
        <div className="flex items-center justify-between mb-8">
          <h1 className="text-3xl md:text-4xl font-bold">
            <MessageCircle className="inline w-8 h-8 mr-3 text-[#25D366]" />
            WhatsApp Leads
          </h1>
          <button
            onClick={fetchData}
            className="p-2 hover:bg-white/10 rounded-lg transition-colors"
          >
            <RefreshCw className="w-5 h-5" />
          </button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
          <div className="glass-card p-5 text-center">
            <Users className="w-7 h-7 text-primary mx-auto mb-2" />
            <p className="text-2xl font-bold">{stats?.total_leads || 0}</p>
            <p className="text-muted-foreground text-sm">Total Leads</p>
          </div>
          <div className="glass-card p-5 text-center">
            <Clock className="w-7 h-7 text-blue-500 mx-auto mb-2" />
            <p className="text-2xl font-bold">{stats?.new_leads || 0}</p>
            <p className="text-muted-foreground text-sm">New</p>
          </div>
          <div className="glass-card p-5 text-center">
            <TrendingUp className="w-7 h-7 text-green-500 mx-auto mb-2" />
            <p className="text-2xl font-bold">{stats?.converted_leads || 0}</p>
            <p className="text-muted-foreground text-sm">Converted</p>
          </div>
          <div className="glass-card p-5 text-center">
            <CheckCircle className="w-7 h-7 text-yellow-500 mx-auto mb-2" />
            <p className="text-2xl font-bold">{stats?.conversion_rate || 0}%</p>
            <p className="text-muted-foreground text-sm">Conversion</p>
          </div>
          <div className="glass-card p-5 text-center">
            <MessageCircle className="w-7 h-7 text-purple-500 mx-auto mb-2" />
            <p className="text-2xl font-bold">{stats?.recent_leads || 0}</p>
            <p className="text-muted-foreground text-sm">This Week</p>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6">
          <button
            onClick={() => setActiveTab('leads')}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
              activeTab === 'leads' ? 'bg-primary text-black' : 'bg-white/5 hover:bg-white/10'
            }`}
          >
            Leads ({leads.length})
          </button>
          <button
            onClick={() => setActiveTab('settings')}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
              activeTab === 'settings' ? 'bg-primary text-black' : 'bg-white/5 hover:bg-white/10'
            }`}
          >
            Settings
          </button>
        </div>

        {activeTab === 'leads' && (
          <>
            {/* Filters */}
            <div className="flex flex-wrap gap-4 mb-6">
              <div className="relative flex-1 min-w-[200px]">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                <input
                  type="text"
                  placeholder="Search by name, phone, or email..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 bg-white/5 border border-white/10 rounded-lg"
                />
              </div>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="px-4 py-2 bg-white/5 border border-white/10 rounded-lg"
              >
                <option value="">All Status</option>
                <option value="new">New</option>
                <option value="contacted">Contacted</option>
                <option value="converted">Converted</option>
                <option value="closed">Closed</option>
              </select>
            </div>

            {/* Leads List */}
            <div className="space-y-4">
              {filteredLeads.length > 0 ? (
                filteredLeads.map((lead) => (
                  <div key={lead.id} className="glass-card p-5">
                    <div className="flex flex-wrap justify-between items-start gap-4">
                      <div className="flex-1 min-w-[200px]">
                        <div className="flex items-center gap-3 mb-2">
                          <h3 className="font-semibold text-lg">{lead.name}</h3>
                          <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${getStatusColor(lead.status)}`}>
                            {lead.status}
                          </span>
                        </div>
                        <div className="space-y-1 text-sm text-muted-foreground">
                          <p className="flex items-center gap-2">
                            <Phone className="w-4 h-4" /> {lead.phone}
                          </p>
                          {lead.email && (
                            <p className="flex items-center gap-2">
                              <Mail className="w-4 h-4" /> {lead.email}
                            </p>
                          )}
                          <p className="flex items-center gap-2">
                            <Clock className="w-4 h-4" /> {new Date(lead.created_at).toLocaleString()}
                          </p>
                        </div>
                        {lead.message && (
                          <p className="mt-2 p-2 bg-white/5 rounded-lg text-sm">
                            "{lead.message}"
                          </p>
                        )}
                      </div>
                      
                      <div className="flex flex-col gap-2">
                        <select
                          value={lead.status}
                          onChange={(e) => handleUpdateLeadStatus(lead.id, e.target.value)}
                          className="px-3 py-1.5 bg-white/5 border border-white/10 rounded-lg text-sm"
                        >
                          <option value="new">New</option>
                          <option value="contacted">Contacted</option>
                          <option value="converted">Converted</option>
                          <option value="closed">Closed</option>
                        </select>
                        
                        <a
                          href={lead.whatsapp_link}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-2 px-3 py-1.5 bg-[#25D366] text-white rounded-lg text-sm hover:bg-[#20BD5A] transition-colors"
                        >
                          <MessageCircle className="w-4 h-4" /> Chat
                        </a>
                        
                        <button
                          onClick={() => handleDeleteLead(lead.id)}
                          className="flex items-center gap-2 px-3 py-1.5 bg-red-500/20 text-red-500 rounded-lg text-sm hover:bg-red-500/30 transition-colors"
                        >
                          <Trash2 className="w-4 h-4" /> Delete
                        </button>
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-center py-12 text-muted-foreground">
                  <MessageCircle className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p>No leads found</p>
                </div>
              )}
            </div>
          </>
        )}

        {activeTab === 'settings' && (
          <div className="glass-card p-6 max-w-2xl">
            <h2 className="text-xl font-semibold mb-6">WhatsApp Settings</h2>
            
            <div className="space-y-4">
              <label className="flex items-center gap-3">
                <input
                  type="checkbox"
                  checked={settings.enabled}
                  onChange={(e) => setSettings({...settings, enabled: e.target.checked})}
                  className="w-5 h-5 rounded"
                />
                <span className="font-medium">Enable WhatsApp Widget</span>
              </label>

              <div>
                <label className="block text-sm font-medium mb-2">Business WhatsApp Number *</label>
                <input
                  type="tel"
                  value={settings.business_phone}
                  onChange={(e) => setSettings({...settings, business_phone: e.target.value})}
                  placeholder="919876543210 (with country code)"
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl"
                />
                <p className="text-xs text-muted-foreground mt-1">Include country code without + (e.g., 919876543210)</p>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Business Name</label>
                <input
                  type="text"
                  value={settings.business_name}
                  onChange={(e) => setSettings({...settings, business_name: e.target.value})}
                  placeholder="Your Business Name"
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Working Hours</label>
                <input
                  type="text"
                  value={settings.working_hours}
                  onChange={(e) => setSettings({...settings, working_hours: e.target.value})}
                  placeholder="Mon-Sat, 9 AM - 6 PM"
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Auto-Reply Message</label>
                <textarea
                  value={settings.auto_reply_message}
                  onChange={(e) => setSettings({...settings, auto_reply_message: e.target.value})}
                  rows={2}
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl"
                />
              </div>

              <button
                onClick={handleSaveSettings}
                disabled={saving}
                className="w-full btn-primary flex items-center justify-center gap-2"
              >
                <Save className="w-5 h-5" />
                {saving ? 'Saving...' : 'Save Settings'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default WhatsAppLeads;
